import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Activity,
  BarChart3,
  Building2,
  ClipboardList,
  FileText,
  LogOut,
  MousePointerClick,
  ShieldCheck,
  Users,
  WalletCards,
  Webhook
} from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { LanguageMenu } from "./components/LanguageMenu";
import { LandingPage } from "./components/LandingPage";
import { LoginView } from "./components/LoginView";
import { WorkspaceView } from "./components/WorkspaceView";
import { Button } from "./components/ui/button";
import { agencyBrands, applyBrandTheme, type AgencyBrand } from "./lib/brand";
import { changeLocale, loadLocaleNamespaces, type LocaleNamespace } from "./lib/i18n";
import { currentDocumentLocale, persistBrowserLocale, type SupportedLocale } from "./lib/preferences";
import { clearSession, restoreSession, type ActorRole, type Session, type Workspace } from "./lib/api";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 120_000,
      retry: 1
    }
  }
});

type WorkspaceSection = "overview" | "work" | "entities" | "reports" | "settings" | "users";

type ShellNavItem = {
  id: WorkspaceSection;
  label: string;
  icon: ReactNode;
};

function normalizeWorkspacePath(pathname: string): string {
  if (pathname === "/app" || pathname === "/app/") {
    return "/app/overview";
  }
  return pathname;
}

function toWorkspaceSection(value: string): value is WorkspaceSection {
  return (["overview", "work", "entities", "reports", "settings", "users"] as const).includes(value as WorkspaceSection);
}

function resolveWorkspaceSection(pathname: string, workspace: Workspace): WorkspaceSection {
  if (!pathname.startsWith("/app/")) {
    return "overview";
  }

  const segment = pathname.replace(/^\/+/, "").split("/")[1];
  const section = toWorkspaceSection(segment) ? segment : "overview";

  if (workspace === "admin" && section === "settings") {
    return "overview";
  }

  if (workspace !== "admin" && section === "users") {
    return "overview";
  }

  return section;
}

function isLoginRoute(pathname: string): boolean {
  return pathname === "/signin" || pathname === "/login" || pathname.startsWith("/app/");
}

function AppContent() {
  const initialPath = normalizeWorkspacePath(window.location.pathname);
  const initialLocale = useMemo(() => currentDocumentLocale(), []);
  const [locale, setLocale] = useState<SupportedLocale>(initialLocale);
  const brand: AgencyBrand = agencyBrands[0];
  const [session, setSession] = useState<Session | null>(null);
  const [isSessionBootstrapping, setIsSessionBootstrapping] = useState(true);
  const [namespaceReady, setNamespaceReady] = useState(false);
  const [currentPath, setCurrentPath] = useState(initialPath);
  const { t } = useTranslation("common");
  const workspace: Workspace | null = session?.workspace ?? null;
  const actorRole: ActorRole = session?.actorRole ?? "platform_support";
  const roleLabel = workspace === "admin" ? t("nav.admin") : workspace === "advertiser" ? t("nav.advertiser") : t("nav.webmaster");
  const navItems = navigationForWorkspace(workspace, t);
  const activeSection = workspace === null ? "overview" : resolveWorkspaceSection(currentPath, workspace);

  useEffect(() => {
    applyBrandTheme(brand);
  }, [brand]);

  useEffect(() => {
    let canceled = false;

    setNamespaceReady(false);
    const namespaces: LocaleNamespace[] = ["common"];

    if (workspace) {
      namespaces.push(workspace);
    }

    loadLocaleNamespaces(locale, namespaces).then(() => {
      if (!canceled) {
        setNamespaceReady(true);
      }
    });

    return () => {
      canceled = true;
    };
  }, [locale, workspace]);

  useEffect(() => {
    let canceled = false;

    setIsSessionBootstrapping(true);
    restoreSession()
      .then((nextSession) => {
        if (!canceled) {
          setSession(nextSession);
        }
      })
      .catch(() => {
        if (!canceled) {
          setSession(null);
        }
      })
      .finally(() => {
        if (!canceled) {
          setIsSessionBootstrapping(false);
        }
      });

    return () => {
      canceled = true;
    };
  }, []);

  useEffect(() => {
    if (!isSessionBootstrapping && session && !session.workspace) {
      clearSession();
      setSession(null);
    }
  }, [isSessionBootstrapping, session]);

  useEffect(() => {
    const syncRoute = () => {
      setCurrentPath(normalizeWorkspacePath(window.location.pathname));
    };

    syncRoute();
    window.addEventListener("popstate", syncRoute);
    return () => {
      window.removeEventListener("popstate", syncRoute);
    };
  }, []);

  function normalizeSignedInPath(nextWorkspace: Workspace, pathname: string): string {
    return isLoginRoute(pathname) ? `/app/${resolveWorkspaceSection(pathname, nextWorkspace)}` : pathname;
  }

  useEffect(() => {
    if (!workspace) {
      return;
    }

    const nextPath = normalizeSignedInPath(workspace, currentPath);
    if (nextPath !== currentPath) {
      window.history.replaceState({}, "", nextPath);
      setCurrentPath(nextPath);
    }
  }, [workspace, currentPath]);

  function navigate(path: string) {
    const normalized = normalizeWorkspacePath(path);
    if (window.location.pathname !== normalized) {
      window.history.pushState({}, "", normalized);
    }
    setCurrentPath(normalized);
  }

  async function handleLocaleChange(nextLocale: SupportedLocale) {
    persistBrowserLocale(nextLocale);
    await changeLocale(nextLocale);
    setLocale(nextLocale);
  }

  function handleLogout() {
    clearSession();
    queryClient.clear();
    setSession(null);
    navigate("/");
  }

  function handleSession(nextSession: Session) {
    queryClient.clear();
    setSession(nextSession);
    navigate("/app/overview");
  }

  if (isSessionBootstrapping) {
    return <main className="workspace-main" aria-busy="true" />;
  }

  if (!session || !workspace) {
    if (isLoginRoute(currentPath)) {
      return (
        <LoginView
          locale={locale}
          onLocaleChange={handleLocaleChange}
          onSession={handleSession}
          onBack={() => navigate("/")}
        />
      );
    }

    return <LandingPage locale={locale} onLocaleChange={handleLocaleChange} onSignIn={() => navigate("/signin")} />;
  }

  return (
    <div className="app-shell">
      <aside className="side-rail">
        <div className="brand-mark" aria-label={brand.name}>
          <span>{brand.mark}</span>
        </div>
        <nav className="rail-nav" aria-label={t("nav.primary")}>
          {navItems.map((item) => (
            <button
              aria-current={activeSection === item.id ? "page" : undefined}
              className="rail-button"
              key={item.id}
              onClick={() => navigate(`/app/${item.id}`)}
              type="button"
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <div className="content-shell">
        <header className="topbar">
          <div className="topbar-identity">
            <span className="eyebrow">{brand.name}</span>
            <strong>{roleLabel}</strong>
            <p>{t("app.scope")} · {session.email}</p>
          </div>
          <div className="topbar-controls">
            <span className={["role-badge", `role-badge-${workspace}`].join(" ")}>
              {actorRole === "platform_admin" ? t("actor.options.platform_admin") : roleLabel}
            </span>
            <LanguageMenu onChange={handleLocaleChange} value={locale} />
            <Button icon={<LogOut aria-hidden="true" size={16} />} onClick={handleLogout} variant="secondary">
              {t("auth.logout")}
            </Button>
          </div>
        </header>

        {namespaceReady ? (
          <WorkspaceView actorRole={actorRole} activeSection={activeSection} onSectionNavigate={navigate} workspace={workspace} />
        ) : (
          <main className="workspace-main" aria-busy="true" />
        )}
      </div>
    </div>
  );
}

function navigationForWorkspace(workspace: Workspace | null, t: (key: string) => string): ShellNavItem[] {
  if (workspace === null) {
    return [];
  }

  const base: ShellNavItem[] = [
    { id: "overview", label: t("nav.overview"), icon: <BarChart3 aria-hidden="true" size={18} /> },
    {
      id: "work",
      label: workspace === "webmaster" ? t("nav.flows") : workspace === "advertiser" ? t("nav.offers") : t("nav.companies"),
      icon:
        workspace === "webmaster" ? (
          <MousePointerClick aria-hidden="true" size={18} />
        ) : workspace === "advertiser" ? (
          <Building2 aria-hidden="true" size={18} />
        ) : (
          <ShieldCheck aria-hidden="true" size={18} />
        )
    },
    { id: "entities", label: t("nav.operations"), icon: <ClipboardList aria-hidden="true" size={18} /> },
    {
      id: "reports",
      label: workspace === "webmaster" ? t("nav.payouts") : workspace === "advertiser" ? t("nav.postbacks") : t("nav.reports"),
      icon:
        workspace === "webmaster" ? (
          <WalletCards aria-hidden="true" size={18} />
        ) : workspace === "advertiser" ? (
          <Webhook aria-hidden="true" size={18} />
        ) : (
          <Activity aria-hidden="true" size={18} />
        )
    }
  ];

  if (workspace === "admin") {
    return [...base, { id: "users", label: t("nav.users"), icon: <Users aria-hidden="true" size={18} /> }];
  }

  return [...base, { id: "settings", label: t("nav.settings"), icon: <FileText aria-hidden="true" size={18} /> }];
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  );
}
